import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { configureAuth, loadPublicConfig, resolveAppUser } from "./auth";
import { useI18n } from "./i18n";
import { isManagement } from "../types";
import { fetchAccountState } from "./account";
import {
  archiveLearner,
  createEvidence,
  createLearner,
  deleteEvidence,
  fetchActivities,
  fetchCompetencies,
  fetchEvidence,
  fetchLearners,
  fetchLevels,
  fetchMastery,
  fetchPlan,
  fetchOrCreatePlan,
  fetchRecentPlans,
  fetchWorkedDates,
  fetchTerms,
  logActivitySession,
  removeActivitySession,
  setPlanItemStatus,
  today,
  updateLearner,
} from "./data";
import type { Level, Term } from "./curriculum";
import { catchUpDay, middayOn, shiftDay } from "./catch-up";
import type {
  Activity,
  AppUser,
  Competency,
  Evidence,
  EvidenceKind,
  Learner,
  LearnerProfileInput,
  MasteryLevel,
  MasteryRow,
  PlanDay,
  PlanItem,
} from "../types";

export type BootState = "loading" | "signed-out" | "unavailable" | "ready";

export type SaveEvidenceInput = {
  activityId: string | null;
  planItemId: string | null;
  kind: EvidenceKind;
  title: string;
  note: string;
  domain: string | null;
  sharedWithCoach: boolean;
  competencyIds: string[];
  rating: MasteryLevel;
  file: File | null;
};

export function useHomeschool() {
  // The curriculum is refetched when the language changes, because the
  // translations are rows rather than a client-side dictionary — switching
  // language re-reads the day, not just the buttons.
  const { locale, t } = useI18n();
  const [boot, setBoot] = useState<BootState>("loading");
  const [supabase, setSupabase] = useState<SupabaseClient | null>(null);
  const [user, setUser] = useState<AppUser | null>(null);

  const [learners, setLearners] = useState<Learner[]>([]);
  const [learnerId, setLearnerId] = useState<string | null>(null);
  const [competencies, setCompetencies] = useState<Competency[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);
  const [terms, setTerms] = useState<Term[]>([]);
  /** Days this child has actually worked — what the term progress counts in. */
  const [daysWorked, setDaysWorked] = useState(0);
  const [activities, setActivities] = useState<Activity[]>([]);

  const [plan, setPlan] = useState<PlanDay | null>(null);
  /** Worked dates as the server last reported them. */
  const [workedDates, setWorkedDates] = useState<string[]>([]);
  /** The last few days' plans, read-only, for the catch-up question. */
  const [recentPlans, setRecentPlans] = useState<PlanDay[]>([]);
  /**
   * Which earlier day to ask about, fixed when the learner loads. Fixed rather
   * than recomputed, so ticking the last activity leaves the question on screen
   * to say "recorded" instead of vanishing under the parent's thumb.
   */
  const [catchUpPlanId, setCatchUpPlanId] = useState<string | null>(null);
  const [evidence, setEvidence] = useState<Evidence[]>([]);
  const [mastery, setMastery] = useState<MasteryRow[]>([]);
  const [learnerLoading, setLearnerLoading] = useState(false);
  const [error, setError] = useState("");
  const [familyLoading, setFamilyLoading] = useState(true);
  const [familyError, setFamilyError] = useState("");
  const familyRequest = useRef(0);
  const pendingItems = useRef(new Set<string>());

  const [date, setDate] = useState(today);
  useEffect(() => {
    const refresh = () => setDate(today());
    const interval = window.setInterval(refresh, 30_000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);
  const learner = useMemo(
    () => learners.find((candidate) => candidate.id === learnerId) ?? null,
    [learners, learnerId],
  );

  // Guards a slow learner load from being applied after the user has already
  // switched to a different child.
  const learnerRequest = useRef(0);

  // -------------------------------------------------------------------------
  // Boot
  // -------------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const config = await loadPublicConfig();
      if (cancelled) return;

      const client = configureAuth(config);
      if (!client) {
        // No Supabase binding — the Worker has not been given its key, or this
        // is `vite dev` with no Worker at all. Say so rather than showing an
        // empty app that looks broken.
        setBoot("unavailable");
        return;
      }
      setSupabase(client);

      const { data } = await client.auth.getSession();
      if (cancelled) return;

      const token = data.session?.access_token;
      if (!token) {
        setBoot("signed-out");
        return;
      }

      // resolveAppUser also finishes an account that never got its parent
      // profile — the families who joined while sign-up was broken were left
      // signed in to exactly that, and this is the next time they knock.
      const appUser = await resolveAppUser(token);
      if (cancelled) return;
      if (!appUser) {
        setBoot("signed-out");
        return;
      }
      setUser(appUser);
      setBoot("ready");
    })().catch(() => { if (!cancelled) setBoot("unavailable"); });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!supabase) return;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        learnerRequest.current += 1;
        familyRequest.current += 1;
        setUser(null);
        setLearners([]);
        setLearnerId(null);
        setPlan(null);
        setEvidence([]);
        setMastery([]);
        setBoot("signed-out");
      }
    });
    return () => subscription.unsubscribe();
  }, [supabase]);

  // -------------------------------------------------------------------------
  // Curriculum and family, once signed in
  // -------------------------------------------------------------------------

  const reloadFamily = useCallback(async () => {
    if (!supabase) return;
    const request = ++familyRequest.current;
    setFamilyLoading(true);
    setFamilyError("");
    try {
      const [nextLearners, nextCompetencies, nextActivities, nextLevels, nextTerms] = await Promise.all([
        fetchLearners(supabase),
        fetchCompetencies(supabase, locale),
        // Retired activities included: an admin has to be able to see and
        // restore what they retired, and a plan built this morning has to keep
        // rendering if an activity in it is retired this afternoon.
        // `chooseActivities` is where retired rows are actually excluded.
        // Admin edits the canonical English fields; never save a localized
        // overlay back over the source curriculum.
        fetchActivities(supabase, user && isManagement(user.role) ? "en" : locale, true),
        fetchLevels(supabase),
        fetchTerms(supabase),
      ]);
      if (request !== familyRequest.current) return;
      setLearners(nextLearners);
      setCompetencies(nextCompetencies);
      setActivities(nextActivities);
      setLevels(nextLevels);
      setTerms(nextTerms);
      setLearnerId((current) =>
        current && nextLearners.some((candidate) => candidate.id === current)
          ? current
          : rememberedLearner(user?.id, nextLearners),
      );
    } catch (cause) {
      if (request === familyRequest.current) setFamilyError(cause instanceof Error ? cause.message : t("error.load"));
    } finally {
      if (request === familyRequest.current) setFamilyLoading(false);
    }
  }, [supabase, locale, t, user]);

  useEffect(() => {
    if (boot === "ready") void reloadFamily();
    return () => { familyRequest.current += 1; };
  }, [boot, reloadFamily]);

  useEffect(() => {
    if (!user || !learnerId) return;
    try { localStorage.setItem(`homeschool-learner:${user.id}`, learnerId); } catch { /* Storage is optional. */ }
  }, [user, learnerId]);

  // -------------------------------------------------------------------------
  // The active learner's day, portfolio and mastery
  // -------------------------------------------------------------------------

  const reloadLearner = useCallback(async () => {
    const request = (learnerRequest.current += 1);
    setPlan(null);
    setEvidence([]);
    setMastery([]);
    setDaysWorked(0);
    setWorkedDates([]);
    setRecentPlans([]);
    setCatchUpPlanId(null);
    if (!supabase || !learner || activities.length === 0) {
      setLearnerLoading(false);
      return;
    }
    setLearnerLoading(true);
    try {
      // Days worked is read BEFORE the plan is generated, so the day the plan
      // is built for is the day the child is about to work rather than one
      // already counted.
      const dates = await fetchWorkedDates(supabase, learner.id);
      const worked = dates.length;
      // Provision the trial before creating a first plan, and don't report an
      // expected expiry as a failed network request. Existing plans stay readable.
      const access = user?.role === "parent" && learner.learning_route === "parent_led"
        ? await fetchAccountState(supabase) : { access: true };
      const planRequest =
        learner.learning_route === "parent_led" && access.access
          ? fetchOrCreatePlan(supabase, learner, date, activities, {
              levels,
              terms,
              daysWorked: worked,
            })
          : fetchPlan(supabase, learner.id, date);
      const [nextPlan, nextEvidence, nextMastery, nextRecent] = await Promise.allSettled([
        planRequest,
        fetchEvidence(supabase, learner.id),
        fetchMastery(supabase, learner.id),
        fetchRecentPlans(supabase, learner.id, shiftDay(date, -3), date),
      ]);
      if (request !== learnerRequest.current) return;
      // A denied new plan must not hide the family's existing learning record.
      setPlan(nextPlan.status === "fulfilled" ? nextPlan.value : null);
      setEvidence(nextEvidence.status === "fulfilled" ? nextEvidence.value : []);
      setMastery(nextMastery.status === "fulfilled" ? nextMastery.value : []);
      setDaysWorked(worked);
      setWorkedDates(dates);
      // Optional: a failed look back just means no question today, never an error.
      const recent = nextRecent.status === "fulfilled" ? nextRecent.value : [];
      setRecentPlans(recent);
      setCatchUpPlanId(catchUpDay(recent, activities, date)?.id ?? null);
      const failed = [nextPlan, nextEvidence, nextMastery].find((result) => result.status === "rejected");
      setError(failed?.status === "rejected"
        ? (failed.reason instanceof Error ? failed.reason.message : t("error.plan"))
        : "");
    } catch (cause) {
      if (request !== learnerRequest.current) return;
      setError(cause instanceof Error ? cause.message : t("error.plan"));
    } finally {
      if (request === learnerRequest.current) setLearnerLoading(false);
    }
  }, [supabase, learner, activities, levels, terms, date, t, user?.role]);

  useEffect(() => {
    void reloadLearner();
    return () => { learnerRequest.current += 1; };
  }, [reloadLearner]);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const toggleItem = useCallback(
    async (itemId: string) => {
      if (!supabase || !plan || !learner || !user) return;
      const item = plan.items.find((candidate) => candidate.id === itemId);
      if (!item || pendingItems.current.has(itemId)) return;
      pendingItems.current.add(itemId);
      const next = item.status === "done" ? "pending" : "done";

      // Optimistic: ticking a moment complete should feel instant, and the only
      // failure mode is a stale tick that the next load corrects.
      setPlan({
        ...plan,
        items: plan.items.map((candidate) =>
          candidate.id === itemId ? { ...candidate, status: next } : candidate,
        ),
      });

      try {
        const saved = await setPlanItemStatus(supabase, itemId, next);
        setPlan((current) =>
          current
            ? {
                ...current,
                items: current.items.map((candidate) => (candidate.id === itemId ? saved : candidate)),
              }
            : current,
        );

        // Bank the minutes, or take them back. The session ledger is what
        // answers "how many hours has this child done this term", which the
        // plan alone cannot: a tick says the activity happened, not when or for
        // how long.
        //
        // Deliberately after the status write and deliberately non-fatal: a
        // failed session row must not roll back a tick the parent can see, and
        // the unique constraint on plan_item_id means a retry can never
        // double-count.
        try {
          if (next === "done") {
            const activity = activities.find((candidate) => candidate.id === item.activity_id);
            await logActivitySession(supabase, {
              learnerId: learner.id,
              planDayId: plan.id,
              planItemId: item.id,
              activityId: item.activity_id,
              title: activity?.title ?? "Activity",
              minutes: activity?.duration_minutes ?? 0,
              recordedBy: user.id,
            });
          } else {
            await removeActivitySession(supabase, item.id);
          }
        } catch {
          // The tick stands. The hours can be corrected by hand on the family
          // page, and telling a parent their tick failed when it did not would
          // be worse than a missing twenty minutes.
        }
      } catch (cause) {
        setPlan((current) =>
          current
            ? { ...current, items: current.items.map((candidate) => (candidate.id === itemId ? item : candidate)) }
            : current,
        );
        setError(cause instanceof Error ? cause.message : t("error.save"));
      } finally {
        pendingItems.current.delete(itemId);
      }
    },
    [supabase, plan, learner, user, activities, t],
  );

  /**
   * Tick or untick an activity on an EARLIER day — the catch-up question.
   *
   * Mirrors toggleItem, with one difference that matters: the completion and the
   * banked minutes are dated to that day, not to now. A tick given this morning
   * for yesterday's counting game is yesterday's work, and the worked-days count
   * that moves the curriculum on has to see it as such.
   */
  const togglePastItem = useCallback(
    async (planDayId: string, itemId: string) => {
      if (!supabase || !learner || !user) return;
      const day = recentPlans.find((candidate) => candidate.id === planDayId);
      const item = day?.items.find((candidate) => candidate.id === itemId);
      if (!day || !item || pendingItems.current.has(itemId)) return;
      pendingItems.current.add(itemId);
      const next = item.status === "done" ? "pending" : "done";
      const when = middayOn(day.plan_date);
      const replace = (replacement: PlanItem) =>
        setRecentPlans((current) =>
          current.map((candidate) =>
            candidate.id !== planDayId
              ? candidate
              : { ...candidate, items: candidate.items.map((entry) => (entry.id === itemId ? replacement : entry)) },
          ),
        );

      replace({ ...item, status: next });
      try {
        replace(await setPlanItemStatus(supabase, itemId, next, when));
        try {
          if (next === "done") {
            const activity = activities.find((candidate) => candidate.id === item.activity_id);
            await logActivitySession(supabase, {
              learnerId: learner.id,
              planDayId: day.id,
              planItemId: item.id,
              activityId: item.activity_id,
              title: activity?.title ?? "Activity",
              minutes: activity?.duration_minutes ?? 0,
              recordedBy: user.id,
              occurredAt: when,
            });
          } else {
            await removeActivitySession(supabase, item.id);
          }
        } catch {
          // As in toggleItem: the tick stands even if the minutes do not.
        }
      } catch (cause) {
        replace(item);
        setError(cause instanceof Error ? cause.message : t("error.save"));
      } finally {
        pendingItems.current.delete(itemId);
      }
    },
    [supabase, learner, user, recentPlans, activities, t],
  );

  const saveEvidence = useCallback(
    async (input: SaveEvidenceInput) => {
      if (!supabase || !learner || !user) throw new Error("Not ready.");
      const request = learnerRequest.current;
      await createEvidence(supabase, {
        learnerId: learner.id,
        createdBy: user.id,
        ...input,
      });
      // Mastery is a derived view, so it can only be re-read — which is the
      // point of deriving it. Re-read the portfolio in the same pass.
      const [nextEvidence, nextMastery] = await Promise.all([
        fetchEvidence(supabase, learner.id),
        fetchMastery(supabase, learner.id),
      ]);
      if (request !== learnerRequest.current) return;
      setEvidence(nextEvidence);
      setMastery(nextMastery);

      if (input.planItemId) {
        const item = plan?.items.find((candidate) => candidate.id === input.planItemId);
        if (item && item.status !== "done") await toggleItem(item.id);
      }
    },
    [supabase, learner, user, plan, toggleItem],
  );

  const removeEvidence = useCallback(
    async (target: Evidence) => {
      if (!supabase || !learner) return;
      const request = learnerRequest.current;
      await deleteEvidence(supabase, target);
      const [nextEvidence, nextMastery] = await Promise.all([
        fetchEvidence(supabase, learner.id),
        fetchMastery(supabase, learner.id),
      ]);
      if (request !== learnerRequest.current) return;
      setEvidence(nextEvidence);
      setMastery(nextMastery);
    },
    [supabase, learner],
  );

  const addLearner = useCallback(
    async (input: LearnerProfileInput) => {
      if (!supabase || !user) throw new Error("Not ready.");
      const { learner: created, request } = await createLearner(supabase, user.id, input);
      setLearners((current) => [...current, created]);
      setLearnerId(created.id);
      return request;
    },
    [supabase, user],
  );

  /**
   * One child from the first-run wizard, saved without making it the active
   * child — the wizard saves brothers and sisters in a row and then opens the
   * app on the FIRST, which addLearner (active = whoever was added last) would
   * get wrong. Resolves with the new learner's id.
   */
  const createChild = useCallback(
    async (input: LearnerProfileInput) => {
      if (!supabase || !user) throw new Error("Not ready.");
      const { learner: created } = await createLearner(supabase, user.id, input);
      setLearners((current) => [...current, created]);
      return created.id;
    },
    [supabase, user],
  );

  const saveLearner = useCallback(
    async (learnerId: string, input: LearnerProfileInput) => {
      if (!supabase) throw new Error("Not ready.");
      const { learner: saved, request } = await updateLearner(supabase, learnerId, input);
      setLearners((current) =>
        current.map((candidate) => (candidate.id === saved.id ? saved : candidate)),
      );
      return request;
    },
    [supabase],
  );

  /**
   * Retire a child, and move to whoever is left.
   *
   * The list is refetched rather than filtered locally so that the active
   * learner cannot end up pointing at a row the app has just stopped reading.
   */
  const removeLearner = useCallback(
    async (targetId: string) => {
      if (!supabase) throw new Error("Not ready.");
      await archiveLearner(supabase, targetId);
      const remaining = await fetchLearners(supabase);
      setLearners(remaining);
      setLearnerId((current) => (current === targetId ? (remaining[0]?.id ?? null) : current));
    },
    [supabase],
  );

  const currentPlan = plan?.learner_id === learnerId && plan?.plan_date === date ? plan : null;

  /**
   * Worked dates as they stand right now, for the streak.
   *
   * The server's list is from load time; the plans held in memory are newer, so
   * for those days their own ticks decide — a tick today adds today, unticking
   * everything takes it away again.
   */
  const liveWorkedDates = useMemo(() => {
    const held = currentPlan ? [...recentPlans, currentPlan] : recentPlans;
    const heldDates = new Set(held.map((day) => day.plan_date));
    const dates = new Set(workedDates.filter((day) => !heldDates.has(day)));
    for (const day of held) if (day.items.some((item) => item.status === "done")) dates.add(day.plan_date);
    return [...dates];
  }, [workedDates, recentPlans, currentPlan]);

  const catchUp = recentPlans.find((candidate) => candidate.id === catchUpPlanId) ?? null;

  const signOut = useCallback(async () => {
    const result = await supabase?.auth.signOut();
    if (result?.error) { setError(result.error.message); return; }
    window.location.reload();
  }, [supabase]);

  /**
   * Carry a fresh sign-in or sign-up straight into the app.
   *
   * The auth listener only handles signing OUT, so nothing else would notice
   * the new session; this reads it, resolves the person exactly as the boot
   * does, and flips to ready — which loads the family. No page reload between
   * "Create account" and "Choose a curriculum". Returns false when it could not
   * finish, so the caller can fall back to a reload.
   */
  const completeSignIn = useCallback(async () => {
    if (!supabase) return false;
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) return false;
    const appUser = await resolveAppUser(token);
    if (!appUser) return false;
    setUser(appUser);
    setBoot("ready");
    return true;
  }, [supabase]);

  return {
    boot,
    supabase,
    user,
    error,
    familyLoading,
    familyError,
    setError,
    date,
    learners,
    learner,
    setLearnerId,
    competencies,
    activities,
    levels,
    terms,
    daysWorked,
    workedDates: liveWorkedDates,
    plan: currentPlan,
    catchUp: catchUp?.learner_id === learnerId ? catchUp : null,
    evidence: evidence.filter(item => item.learner_id === learnerId),
    mastery: mastery.filter(item => item.learner_id === learnerId),
    learnerLoading,
    reloadFamily,
    reloadLearner,
    toggleItem,
    togglePastItem,
    saveEvidence,
    removeEvidence,
    addLearner,
    createChild,
    saveLearner,
    removeLearner,
    signOut,
    completeSignIn,
  };
}

export type Homeschool = ReturnType<typeof useHomeschool>;

function rememberedLearner(userId: string | undefined, learners: Learner[]) {
  try {
    const id = userId ? localStorage.getItem(`homeschool-learner:${userId}`) : null;
    if (learners.some(learner => learner.id === id)) return id;
  } catch { /* A blocked store should not block the family. */ }
  return learners[0]?.id ?? null;
}
